import { getLogger, trackHeapEvent } from "@hoteltradergit/ht-telemetry-ui-lib";

// web-vitals 6. The v2 API this file used (`getCLS`/`getFID`/…) is gone: v3
// renamed every getter to `on*`, and v4 dropped FID entirely in favour of INP,
// which is the metric Google now scores. These five are the full v6 surface.
const COLLECTORS = ["onCLS", "onFCP", "onINP", "onLCP", "onTTFB"];

/**
 * Reports Core Web Vitals to Heap.
 *
 * Previously this was CRA boilerplate invoked as `reportWebVitals()` with no
 * argument, so its `onPerfEntry instanceof Function` guard short-circuited and
 * web-vitals was never even imported — no metric was ever collected. It now
 * defaults to a real sink so calling it bare does what the name implies.
 *
 * Each collector fires at most once per page load (INP and CLS settle late,
 * typically on visibility change), so this is a handful of events per session,
 * not a stream.
 *
 * @param {(metric: object) => void} [onPerfEntry] override sink; defaults to Heap.
 */
const reportWebVitals = (onPerfEntry) => {
  const report =
    typeof onPerfEntry === "function"
      ? onPerfEntry
      : ({ name, value, rating, delta, id, navigationType }) => {
          trackHeapEvent("Web Vital", {
            metric_name: name,
            // CLS is unitless; the rest are milliseconds. Rounded because Heap
            // buckets numeric properties and sub-millisecond precision is noise.
            metric_value:
              name === "CLS" ? Number(value.toFixed(4)) : Math.round(value),
            metric_rating: rating, // "good" | "needs-improvement" | "poor"
            metric_delta:
              name === "CLS" ? Number(delta.toFixed(4)) : Math.round(delta),
            metric_id: id,
            navigation_type: navigationType ?? null,
          });
        };

  // Loaded lazily so web-vitals stays out of the initial bundle; it only needs
  // to exist by the time the browser has paint/interaction data to report.
  import("web-vitals")
    .then((webVitals) => {
      COLLECTORS.forEach((collector) => webVitals[collector](report));
    })
    .catch((error) => {
      // Never let telemetry break the app, and follow the house rule of routing
      // errors through the logger rather than console.error.
      getLogger().error("web-vitals failed to load", error);
    });
};

export default reportWebVitals;
